import { formatISO, parseISO } from 'date-fns';
import * as React from 'react';
import type { VoidFunctionComponent } from 'react';

import { prettyMDY } from '../../../../shared/utils/dates';
import Button from '../../generic/Button/Button';
import ModalDateRangePicker from '../../modals/ModalDateRangePicker';
import type { SnapshotFilters } from '../../../../shared/issuesV2/snapshots';

type Props = {
  filters: SnapshotFilters;
  setDates: (start: Date, end: Date) => void;
  isDisabled: boolean;
};

/**
 * Dropdown menu for filtering by Date on the Issue Overview page.
 */
const DateFilter: VoidFunctionComponent<Props> = ({ filters, setDates, isDisabled }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <Button type="secondary" onClick={() => setIsOpen(true)} isDisabled={isDisabled}>
        {prettyMDY(filters.start, true)} - {prettyMDY(filters.end, true)}
      </Button>
      <ModalDateRangePicker
        show={isOpen}
        startDate={filters.start}
        endDate={filters.end}
        // The date picker gives us dates at midnight, so we round to the end of the day.
        onSetDateRange={({ startDate, endDate }) => {
          // The date picker returns dates in the local timezone, but we want to handle them as UTC.
          // By formatting the date as YYYY-MM-DD, we can then parse it as UTC
          // without the date/time being changed to account for offset.
          const startDateString = formatISO(startDate, { representation: 'date' });
          const endDateString = formatISO(endDate, { representation: 'date' });
          const utcConvertedStartDate = parseISO(`${startDateString}T00:00:00.000Z`);
          const utcConvertedEndDate = parseISO(`${endDateString}T00:00:00.000Z`);
          setDates(utcConvertedStartDate, utcConvertedEndDate);
        }}
        onHide={() => setIsOpen(false)}
      />
    </>
  );
};

export default DateFilter;
